import React, { useCallback, useRef } from 'react';

import { CursorConfig } from 'atoms/cursor/config';
import {
	type SpringPullState,
	useSpringTransform,
} from 'atoms/cursor/hooks/spring';

function getCursorAttractables(): HTMLElement[] {
	return Array.from(
		document.querySelectorAll<HTMLElement>('[data-cursor-magnetic]')
	);
}

function distanceToEdge(
	mx: number,
	my: number,
	x: number,
	y: number,
	w: number,
	h: number
): number {
	const dx = Math.max(0, Math.abs(mx - x) - w / 2);
	const dy = Math.max(0, Math.abs(my - y) - h / 2);

	return Math.sqrt(dx * dx + dy * dy);
}

function getOriginalCenter(
	node: HTMLElement,
	state: SpringPullState | undefined
): Rect {
	const r = node.getBoundingClientRect();
	const ox = state ? state.cx : 0;
	const oy = state ? state.cy : 0;

	return {
		x: r.left + r.width / 2 - ox,
		y: r.top + r.height / 2 - oy,
		w: r.width,
		h: r.height,
	};
}

interface UseCursorAttractionReturnType {
	activeNode: React.RefObject<Nullable<HTMLElement>>;
	tick: () => void;
	update: (x: number, y: number) => void;
}

export function useCursorAttraction(): UseCursorAttractionReturnType {
	const springs = useSpringTransform();
	const activeNode = useRef<Nullable<HTMLElement>>(null);

	const update = useCallback(
		(mx: number, my: number): void => {
			const nodes = getCursorAttractables();

			let best: Nullable<HTMLElement> = null;
			let bestD = Infinity;

			for (const node of nodes) {
				const c = getOriginalCenter(node, undefined);
				const d = distanceToEdge(mx, my, c.x, c.y, c.w, c.h);

				if (d < CursorConfig.magneticRadius && d < bestD) {
					best = node;
					bestD = d;
				}
			}

			if (activeNode.current && activeNode.current !== best) {
				springs.release(activeNode.current);
			}

			if (best) {
				const c = getOriginalCenter(best, undefined);
				const edge = distanceToEdge(mx, my, c.x, c.y, c.w, c.h);
				const prox = Math.max(0, 1 - edge / CursorConfig.magneticRadius);

				let px = (mx - c.x) * CursorConfig.pullStrength * prox;
				let py = (my - c.y) * CursorConfig.pullStrength * prox;

				const pd = Math.sqrt(px * px + py * py);

				if (pd > CursorConfig.magneticRadius) {
					const cl = CursorConfig.magneticRadius / pd;

					px *= cl;
					py *= cl;
				}

				springs.pull(best, px, py);
			}

			activeNode.current = best;
		},
		[springs]
	);

	return { activeNode, tick: springs.tick, update };
}
